import {Flex, Modal, Paper, SegmentedControl, Select, Space} from "@mantine/core";
import { EyeSlash as IconEyeOff } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useMemo, useState} from "react";
import {Row, Col} from "../../../components/Flex/Flex";
import TravelPlanner from "../../../components/TravelPlanner/TravelPlanner.tsx";
import style from "../../../style/Map.module.css"
import {useSWCLocation} from "../../../utils/api";
import {UserLocation} from "../../../utils/MapUtils.tsx";
import {modalNoBlockZoom} from "../../../utils/modals";


const optModes = [
    {label: 'Heatmap', value: 'heat'},
    {label: 'Shield Status', value: 'shield'},
    {label: 'System type', value: 'asteroid'}
]
const optSizes = [
    {label: 'Small', value: 'sm'},
    {label: 'Normal', value: 'md'},
    {label: 'Big', value: 'lg'},
]

export default function ViewMap ({filteredData, filterElement, toggleElement}) {
    const [systemDetails, setSystemDetails] = useState(null);
    const [mode, setMode] = useState('heat');
    const [size, setSize] = useState('md'); // size for dots
    const {location: userLocation} = useSWCLocation();

    const [locations, maxOwners] = useMemo(() => {
        if (filteredData === null) {
            return [];
        }
        let systems = {};
        filteredData.forEach(tavern => {
            if(typeof systems[tavern.system.id] === 'undefined') {
                let s = {
                    ...tavern.system,
                    count: 1,
                    shield: tavern.shield,
                    isAsteroid: tavern.system.name.includes('Asteroid Field')
                }
                s.taverns = [tavern];

                systems[s.id] = s;

            } else {
                // just update
                let s = systems[tavern.system.id];
                s.count += 1;
                if(s.shield !== tavern.shield) {
                    if(s.shield === 'Unknown') {
                        s.shield = tavern.shield;
                    } else if(tavern.shield !== 'Unknown') {
                        s.shield = 'Conflicting info';
                    }
                }
                s.taverns = [...s.taverns, tavern];
                systems[s.id] = s;
            }

        });

        systems = Object.values(systems);

        systems = systems.map(s => ({...s, owners: new Set(s.taverns.map(t => t.owner.id)).size}));
        let max = systems.reduce((m, s) => Math.max(m, s.owners), 0);

        systems = systems.map(s => {
            let norm;
            if(max === 1) {
                norm = 0;
            } else if(max<3) {
                norm = s.owners / 3;
            } else {
                norm = s.owners/max;
            }
            return {...s, norm};
        })

        return [systems, max];

    }, [filteredData]);

    // TODO: fix the ordering between md and small screens
    return (
        <div>
            <Paper>
                <Row justify="between">
                    <Col className="flex-order-0">
                        {toggleElement}
                    </Col>
                    <Col className="flex-order-2 md:flex-order-1">
                        {filterElement}
                    </Col>
                    <Col className="md:flex-grow-1" order={2}/>
                    <Col className="flex-order-3">
                        <SegmentedControl value={mode} onChange={setMode} data={optModes}/>
                    </Col>
                    <Col className="md:flex-grow-1" order={3} />
                    <Col className="flex-order-1 md:flex-order-4">
                        <Select data={optSizes} value={size} onChange={v => setSize(v)} placeholder="Dot size" style={{width: 120}} />
                    </Col>
                </Row>
            </Paper>
            <Space h="md" />
            <Flex justify="center">
                <div className={`${style.wrapper} mx-auto`}>
                    {mode === 'heat' ? <LegendHeatmap max={maxOwners} location={userLocation} /> : null}
                    {mode === 'shield' ? <LegendShield location={userLocation} /> : null}
                    {mode === 'asteroid' ? <LegendAsteroid location={userLocation} /> : null}
                    <div className={style.dotWrapper}>
                        {userLocation ? <UserLocation location={userLocation} /> : null}
                        {locations.map(system => <HeatDot key={system.id} system={system} openModal={setSystemDetails} mode={mode} size={size}/>)}
                    </div>
                </div>
            </Flex>
            {systemDetails !== null ? <SystemDetails system={systemDetails} onClose={() => setSystemDetails(null)}/> : null}
        </div>
    )
}


const HeatDot = ({system, openModal, mode, size}) => {
    let addStyle;
    let addClass = '';
    let title = system.name;
    if(mode === 'heat') {
        addStyle = {
            background: "hsl(" + ((1 - system.norm) * 130) + ", 100%, 40%)",
            borderColor: "hsl(" + ((1 - system.norm) * 130) + ", 100%, 20%)",
            boxShadow: "0 0 var(--blur) 2px hsl(" + ((1 - system.norm) * 130) + ", 100%, 30%)"
        }
        title += ' ('+system.owners+' networks)';
    } else if(mode === 'shield') {
        if(system.shield === 'Shielded') {
            addClass = style.dotBlue;
        } else if(system.shield === 'Unshielded') {
            addClass = style.dotGreen;
        } else if(system.shield === 'Open') {
            addClass = style.dotGreen;
        } else if(system.shield === 'Conflicting info') {
            addClass = style.dotRed;
        } else if(system.shield === 'Unknown') {
            addClass = style.dotYellow;
        }
        title += ' ('+system.shield+')';
    } else if(mode==='asteroid') {
        addClass = system.isAsteroid ? style.dotRed : style.dotBlue;
    }


    return <div className={`${style.system} size-${size}`} style={{bottom: getPos(system.y), left: getPos(system.x)}} title={title} onClick={() => openModal(system)}>
        <div className={`${style.dotHeat} ${addClass}`} style={addStyle}/>
    </div>
}

const getPos = (coord) => {
    let perc = (parseInt(coord) + 500) / 10;
    return `${perc}%`;
}


const bodyLocation = (row) => {
    return (<div>
        <div>{row.planet.name} <small>({row.planet.x},{row.planet.y})</small></div>
        <div>{row.city.name} <small>({row.city.x},{row.city.y})</small></div>
        <div>Ground: <small>({row.x},{row.y})</small></div>
    </div>)
}
const bodyActions = (row) => {
    return <TravelPlanner system={row.system} space={row.planet} surface={row.city} ground={row} />
}
const bodyPrivate = (row) => {
    return row.private ? <IconEyeOff size={16} title="Private" /> : null;
}

const SystemDetails = ({system, onClose}) => {

    const columns = [
        {accessor: 'private', title: '', render: bodyPrivate},
        {accessor: 'name', title: 'Name'},
        {accessor: 'owner.name', title: 'Owner'},
        {accessor: 'location', title: 'Location', render: bodyLocation},
        {accessor: 'access', title: 'Access'},
        {accessor: 'shield', title: 'Shield status'},
        {accessor: 'notes', title: 'Notes'},
        {accessor: 'actions', title: '', render: bodyActions},
    ];

    return (
        <Modal
            opened={true}
            onClose={onClose}
            title={`${system.name} (${system.x}, ${system.y})`}
            size="xl"
            removeScrollProps={modalNoBlockZoom}
        >
            <DataTable
                records={system.taverns}
                idAccessor="id"
                columns={columns}
                noRecordsText="No taverns in this system"
            />

        </Modal>
    );
}

const LegendShield = ({location}) => {
    return (
        <div className={style.legend}>
            {location ? <div>Current location: <div className={style.dotPurple} /></div> : null}
            <div>Shielded: <div className={style.dotBlue}/></div>
            <div>Unshielded/Open: <div className={style.dotGreen}/></div>
            <div>Unknown: <div className={style.dotYellow}/></div>
            <div>Conflicting info: <div className={style.dotRed}/></div>
        </div>
    )
}
const LegendAsteroid = ({location}) => {
    return (
        <div className={style.legend}>
            {location ? <div>Current location: <div className={style.dotPurple} /></div> : null}
            <div>Common system: <div className={style.dotBlue}/></div>
            <div>Asteroid field: <div className={style.dotRed}/></div>
        </div>
    )
}

const LegendHeatmap = ({max, location}) => {

    if(max === 1) {
        let csingle = {
            background: "hsl(" + (130) + ", 100%, 40%)",
            borderColor: "hsl(" + (130) + ", 100%, 20%)",
            boxShadow: "0 0 var(--blur) 2px hsl(" + (130) + ", 100%, 30%)"
        }
        return (
            <div className={style.legend}>
                {location ? <div>Current location: <div className={style.dotPurple} /></div> : null}
                <div>1 network: <div className={style.dotHeat} style={csingle}/></div>
            </div>
        )
    }
    let avg = Math.round(max/2);
    let normMax = 1;
    if(max < 3) {
        normMax = max/3;
        max = 3;
    }
    let normAvg = avg/max;
    let normMin = 1/max;
    let cmax = {
        background: "hsl(" + ((1 - normMax) * 130) + ", 100%, 40%)",
        borderColor: "hsl(" + ((1 - normMax) * 130) + ", 100%, 20%)",
        boxShadow: "0 0 var(--blur) 2px hsl(" + ((1 - normMax) * 130) + ", 100%, 30%)"
    }

    let cavg = {
        background: "hsl(" + ((1 - normAvg) * 130) + ", 100%, 40%)",
        borderColor: "hsl(" + ((1 - normAvg) * 130) + ", 100%, 20%)",
        boxShadow: "0 0 var(--blur) 2px hsl(" + ((1 - normAvg) * 130) + ", 100%, 30%)"
    }
    let cmin = {
        background: "hsl(" + ((1 - normMin) * 130) + ", 100%, 40%)",
        borderColor: "hsl(" + ((1 - normMin) * 130) + ", 100%, 20%)",
        boxShadow: "0 0 var(--blur) 2px hsl(" + ((1 - normMin) * 130) + ", 100%, 30%)"
    }

    return (
        <div className={style.legend}>
            <div>{max} networks: <div className={style.dotHeat} style={cmax}/></div>
            <div>{avg} networks: <div className={style.dotHeat} style={cavg}/></div>
            <div>1 network: <div className={style.dotHeat} style={cmin}/></div>
        </div>
    )
}
